import type { ComponentProps } from "react";
import { DeleteOutlined, DownOutlined, ExclamationCircleFilled, PauseCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { App, Button, Dropdown } from "antd";

import type { ScheduleStatus } from "@api/types";
import { HOLD as SCHEDULE_STATUS_HOLD } from "@api/services/transactions/shapes/ScheduleStatusEnum";

const LABEL_SUBMIT = "Publish";
const LABEL_UPDATE = "Update";
const LABEL_DELETE_SCHEDULE = "Delete";
const LABEL_ENABLE_SCHEDULE = "Run";
const LABEL_DISABLE_SCHEDULE = "Pause";
const LABEL_CONFIRM_DELETE_SCHEDULE_TITLE = "Delete Schedule";
const LABEL_CONFIRM_DELETE_SCHEDULE_CONTENT = "Are you sure you want to delete this schedule? This action cannot be undone.";
const LABEL_YES = "Yes";

type Props = {
  scheduleStatus: ScheduleStatus;
  isUpdate: boolean;
  isLoading: boolean;
  onDelete: () => void;
  onEnable: () => void;
  onDisable: () => void;
};

type SubmitDropdownButtonProps = Omit<
  ComponentProps<typeof Dropdown.Button>,
  "children"
>;

type SubmitPrimaryButtonProps = Omit<ComponentProps<typeof Button>, "children">;

const SubmitButton = ({
  isUpdate,
  isLoading,
  onDelete,
  scheduleStatus,
  onEnable,
  onDisable,
}: Props) => {
  const { modal } = App.useApp();

  const isHold = scheduleStatus === SCHEDULE_STATUS_HOLD;

  const confirmDelete = () => {
    modal.confirm({
      icon: <ExclamationCircleFilled />,
      title: LABEL_CONFIRM_DELETE_SCHEDULE_TITLE,
      content: LABEL_CONFIRM_DELETE_SCHEDULE_CONTENT,
      okText: LABEL_YES,
      okType: "danger",
      onOk: () => onDelete(),
    });
  };

  if (isUpdate) {
    const enableItem = {
      key: "enable",
      icon: <PlayCircleOutlined />,
      label: LABEL_ENABLE_SCHEDULE,
      onClick: onEnable,
    };

    const disableItem = {
      key: "disable",
      icon: <PauseCircleOutlined />,
      label: LABEL_DISABLE_SCHEDULE,
      onClick: onDisable,
    };

    const deleteItem = {
      key: "delete",
      icon: <DeleteOutlined />,
      label: LABEL_DELETE_SCHEDULE,
      danger: true,
      onClick: () => confirmDelete(),
    };

    const dropdownButtonProps: SubmitDropdownButtonProps = {
      type: "primary",
      icon: <DownOutlined />,
      menu: {
        items: isHold 
          ? [ enableItem, deleteItem ] 
          : [ disableItem, deleteItem ],
      },
      htmlType: "submit",
      loading: isLoading,
      disabled: isLoading,
      trigger: ["click"],
    };

    return (
      <Dropdown.Button {...dropdownButtonProps}>
        {LABEL_UPDATE}
      </Dropdown.Button>
    );
  }

  const buttonProps: SubmitPrimaryButtonProps = {
    type: "primary",
    htmlType: "submit",
    loading: isLoading,
    disabled: isLoading,
  };

  return (
    <Button {...buttonProps}>
      {LABEL_SUBMIT}
    </Button>
  );
};

export default SubmitButton;
